import { Box, CircularProgress, DialogContentText } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { useFormik } from "formik"
import { useAppDispatch } from "hooks"
import { useState } from "react"
import organizationService from "services/organizationService"
import { addOrganization } from "slices/organizationSlice"
import type { ICreateOrganization } from "types/organizationTypes"
import * as Yup from "yup"

export function CreateOrganizationDialog() {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)

  const dispatch = useAppDispatch()

  const initialValues: ICreateOrganization = {
    name: "",
    ownerId: "",
  }

  const createOrganizationValidationScheme = Yup.object({
    name: Yup.string()
      .required("Please enter organization name")
      .min(4, "Minimum name length is 4 characters")
      .max(64, "Maximum name length is 64 characters"),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: createOrganizationValidationScheme,
    onSubmit: (values) => {
      setIsLoading(true)
      const newOrganization: ICreateOrganization = {
        name: values.name,
        ownerId: "",
      }

      organizationService
        .createOrganization(newOrganization)
        .then((resp) => {
          dispatch(addOrganization(resp))
          setIsLoading(false)
          handleClose()
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <Button data-testid="createOrganizationButton" variant="contained" onClick={handleClickOpen}>
        create organization
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Create Organization</DialogTitle>
          <DialogContent>
            <DialogContentText>Please enter new organization's name.</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Name"
              type="name"
              fullWidth
              variant="standard"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </DialogContent>
          <DialogActions>
            <Button data-testid="cancelButton" onClick={handleClose}>
              Cancel
            </Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button data-testid="createButton" type="submit" disabled={isLoading}>
                Create
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>{" "}
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
